import {Component, Inject, OnInit, ViewEncapsulation} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {FormOptionsLists} from '../../../../shared/FormOptionsLists';
import {NotificationService} from '../../../../services/common/notification.service';
import {SharedService} from '../../../../services/common/shared.service';
import {SelectDialogComponent} from '../../../select/select-dialog/select-dialog.component';
import {References} from '../../../../dtos/references';
import {GlobalCanDoStatementService} from '../../../../services/can-do-statement/global-can-do-statement.service';
import {DetailedCanDoStatementService} from '../../../../services/can-do-statement/detailed-can-do-statement.service';
import {CanDoStatementEntry} from '../../../../dtos/can-do-statement/entry/can-do-statement-entry';
import {CanDoStatementEntryCreationDto} from '../../../../dtos/can-do-statement/entry/can-do-statement-entry-creation-dto';

export interface NewCanDoStatementDialogData {
  id?: number;
  service: GlobalCanDoStatementService | DetailedCanDoStatementService;
}

@Component({
  selector: 'app-new-can-do-statement',
  templateUrl: './new-can-do-statement.component.html',
  styleUrls: ['./new-can-do-statement.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class NewCanDoStatementComponent implements OnInit {

  formOptionsLists = new FormOptionsLists();
  levelList = this.formOptionsLists.levelList.filter(o => o !== 'NONE');
  modeList = this.formOptionsLists.modeList;
  activityList = this.formOptionsLists.activityList;

  formGroup: UntypedFormGroup;
  selectedCategory: {id: number; name: string} | null = null;
  references: References;

  edit = false;
  id: number | null = null;
  existingEntry: CanDoStatementEntry | null = null;

  private service: GlobalCanDoStatementService | DetailedCanDoStatementService;
  flavor: 'global' | 'detailed';

  constructor(
    private fb: UntypedFormBuilder,
    private dialogRef: MatDialogRef<NewCanDoStatementComponent>,
    private dialog: MatDialog,
    private notificationService: NotificationService,
    private sharedService: SharedService,
    @Inject(MAT_DIALOG_DATA) public data: NewCanDoStatementDialogData
  ) {
    this.service = data.service;
    this.flavor = data.service instanceof DetailedCanDoStatementService ? 'detailed' : 'global';
  }

  ngOnInit(): void {
    this.formGroup = this.fb.group({
      nameCtrl: ['', Validators.required],
      receptive: [null],
      productive: [null],
      mode: [null],
      activity: [null],
      categoryId: [{value: null, disabled: true}, Validators.required]
    });

    if (this.data?.id) {
      this.edit = true;
      this.id = this.data.id;
      this.service.getEntry(this.id).subscribe({
        next: (entry: CanDoStatementEntry) => {
          this.references = this.sharedService.extractReferences(entry)['Verweise'];
          this.existingEntry = entry;
          this.setForm(entry);
        },
        error: (error) => this.notificationService.openSnackBar(error)
      });
    }
  }

  setForm(entry: CanDoStatementEntry): void {
    if (entry.category) {
      this.selectedCategory = {id: entry.category.id, name: entry.category.name};
      this.formGroup.controls['categoryId'].setValue(entry.category.id);
    }
    this.formGroup.patchValue({
      nameCtrl: entry.name,
      receptive: entry.receptive ?? null,
      productive: entry.productive ?? null,
      mode: entry.mode ?? null,
      activity: entry.activity ?? null
    });
  }

  openCategorySelectionDialog(): void {
    const selectionEntity = this.flavor === 'detailed' ? 'det_can_statements_category' : 'glob_can_statements_category';
    const columnPrefix = this.flavor === 'detailed' ? 'det-can-cat' : 'glob-can-cat';
    const dialogRef = this.dialog.open(SelectDialogComponent, {
      data: {
        selectedObject: this.selectedCategory,
        displayedColumns: ['single-select', columnPrefix + '-name', columnPrefix + '-parent'],
        selectionEntity: selectionEntity,
        dialogTitle: 'Thema auswählen',
        selectionMode: 'single-select'
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result !== undefined) {
        if (result && result.length > 0 && result[0]) {
          this.selectedCategory = result[0];
          this.formGroup.controls['categoryId'].setValue(result[0].id);
        } else {
          this.selectedCategory = null;
          this.formGroup.controls['categoryId'].setValue(null);
        }
      }
    });
  }

  handleReferenceChange($event: References) {
    this.references = $event;
  }

  save(): void {
    if (this.formGroup.controls['nameCtrl'].invalid || !this.selectedCategory) {
      this.notificationService.openSnackBar('Bitte Name und Thema angeben.');
      return;
    }
    const v = this.formGroup.value;
    const dto = new CanDoStatementEntryCreationDto(
      this.id,
      v.nameCtrl,
      this.selectedCategory.id,
      v.receptive || null,
      v.productive || null,
      v.mode || null,
      v.activity || null,
      this.sharedService.getIds(this.references?.vocabCategories),
      this.sharedService.getIds(this.references?.vocabEntries),
      this.sharedService.getIds(this.references?.functionalGrammarCategories),
      this.sharedService.getIds(this.references?.functionalGrammarEntries),
      this.sharedService.getIds(this.references?.systemicGrammarCategories),
      this.sharedService.getIds(this.references?.systemicGrammarEntries),
      this.sharedService.getIds(this.references?.textPatterns),
      this.sharedService.getIds(this.references?.textTypes),
      this.sharedService.getIds(this.references?.detailedCanDoStatementCategories),
      this.sharedService.getIds(this.references?.detailedCanDoStatementEntries),
      this.sharedService.getIds(this.references?.globalCanDoStatementCategories),
      this.sharedService.getIds(this.references?.globalCanDoStatementEntries),
      this.sharedService.getIds(this.references?.verbalActionCategories),
      this.sharedService.getIds(this.references?.verbalActionEntries)
    );

    const request = this.edit
      ? this.service.updateEntry(this.id, dto)
      : this.service.createEntry(dto);

    request.subscribe({
      next: (result) => {
        this.notificationService.openSnackBar(
          'Kannbeschreibung "' + result.name + '" erfolgreich ' + (this.edit ? 'aktualisiert' : 'erstellt')
        );
        this.dialogRef.close(result);
      },
      error: (error) => this.notificationService.openSnackBar(error)
    });
  }
}
